import { createTag } from '@frontend/api-client/utils';
import type { BaseApi } from '@frontend/api-client/utils/types';
import type {
  CreateSecureNoteRequest,
  CreateSecureNoteResponse,
  DeleteSecureNoteRequest,
  DeleteSecureNoteResponse,
  ListSecureNotesResponse,
  ReplaceSecureNoteContentRequest,
  ReplaceSecureNoteContentResponse,
  RetrieveSecureNoteContentResponse,
} from '@frontend/api-types';
import { convertArrayObjectKeysCase, convertObjectKeysCase } from '@frontend/duck-tape';
import { SECURE_NOTES_ENDPOINTS } from './endpoints';

export const getSecureNotesApi = (baseApi: BaseApi) =>
  baseApi.injectEndpoints({
    endpoints: (builder) => ({
      createSecureNote: builder.mutation<CreateSecureNoteResponse, CreateSecureNoteRequest>({
        invalidatesTags: [createTag({ id: 'LIST', type: 'SecureNote' })],
        query: (payload) => ({
          body: payload,
          method: 'POST',
          url: SECURE_NOTES_ENDPOINTS.createSecureNoteContent(),
        }),
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as CreateSecureNoteResponse,
      }),
      deleteSecureNote: builder.mutation<DeleteSecureNoteResponse, DeleteSecureNoteRequest>({
        invalidatesTags: (_result, _error, { secureNoteId }) => [
          createTag({ id: secureNoteId, prefix: 'secureNoteId', type: 'SecureNote' }),
          createTag({ id: 'LIST', type: 'SecureNote' }),
        ],
        query: (payload) => ({
          method: 'DELETE',
          url: SECURE_NOTES_ENDPOINTS.deleteSecureNote(payload),
        }),
      }),
      listSecureNotes: builder.query<ListSecureNotesResponse, { clientId: string }>({
        providesTags: [createTag({ id: 'LIST', type: 'SecureNote' })],
        query: SECURE_NOTES_ENDPOINTS.listSecureNotes,
        transformResponse: (response: GenericRecord[]) =>
          convertArrayObjectKeysCase(response, 'camelCase') as ListSecureNotesResponse,
      }),
      replaceSecureNoteContent: builder.mutation<ReplaceSecureNoteContentResponse, ReplaceSecureNoteContentRequest>({
        invalidatesTags: (_result, _error, { secureNoteId }) => [
          createTag({ id: secureNoteId, prefix: 'secureNoteId', type: 'SecureNote' }),
          createTag({ id: 'LIST', type: 'SecureNote' }),
        ],
        query: ({ formData, secureNoteId }) => ({
          body: formData,
          method: 'PUT',
          url: SECURE_NOTES_ENDPOINTS.replaceSecureNoteContent(secureNoteId),
        }),
        transformResponse: (review: GenericRecord) =>
          convertObjectKeysCase(review, 'camelCase') as ReplaceSecureNoteContentResponse,
      }),
      retrieveSecureNoteContent: builder.query<RetrieveSecureNoteContentResponse, string>({
        providesTags: (_result, _error, secureNoteId) => [
          createTag({ id: secureNoteId, prefix: 'secureNoteId', type: 'SecureNote' }),
        ],
        query: (secureNoteId) => SECURE_NOTES_ENDPOINTS.retrieveSecureNoteContent(secureNoteId),
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as RetrieveSecureNoteContentResponse,
      }),
    }),
    overrideExisting: true,
  });
